import { postCommunication } from '../lib/communicationRequests';
// hooks/useHr.ts

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import type {
  TeamMember,
  TeamMemberListItem,
  TeamMemberFilters,
  CreateTeamMemberRequest,
  OnboardingPackage,
  OnboardingPackageListItem,
  CreateOnboardingPackageRequest,
  OnboardingSession,
  OnboardingSessionListItem,
  InviteNewHireRequest,
  InviteNewHireResponse,
  PublicOnboardingSession,
  TrainingLibraryItem,
  TrainingLibraryItemListItem,
  CreateTrainingItemRequest,
  TrainingAssignment,
  TrainingAssignmentListItem,
  CreateTrainingAssignmentRequest,
  TrainingProgress,
  TrainingProgressSummary,
  TrainingCertificate,
  PublicTrainingProgress,
  SignOnboardingDocumentRequest,
  SignOnboardingDocumentResponse,
  HrSettings,
  UpdateHrSettingsRequest,
} from '../types/hr';

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ============================================================================
// Team Member Hooks
// ============================================================================

export const useTeamMembers = (filters?: TeamMemberFilters) => {
  return useQuery({
    queryKey: ['team-members', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.member_type) params.append('member_type', filters.member_type);
      if (filters?.department) params.append('department', filters.department);
      if (filters?.job_title) params.append('job_title', filters.job_title);
      if (filters?.start_from) params.append('start_from', filters.start_from);
      if (filters?.start_to) params.append('start_to', filters.start_to);
      if (filters?.search) params.append('search', filters.search);
      if (filters?.page) params.append('page', filters.page.toString());

      const response = await apiClient.get<TeamMemberListItem[] | PaginatedResponse<TeamMemberListItem>>(
        `/api/hr/team-members/?${params.toString()}`
      );
      const data = response.data;
      // Return the envelope, not just the rows: dropping `count` left the
      // directory with no way to page past the first 20 members.
      if (Array.isArray(data)) {
        return { count: data.length, results: data, next: null, previous: null } as PaginatedResponse<TeamMemberListItem>;
      }
      return data;
    },
  });
};

export const useTeamMember = (id: string) => {
  return useQuery({
    queryKey: ['team-member', id],
    queryFn: async () => {
      const response = await apiClient.get<TeamMember>(`/api/hr/team-members/${id}/`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useCreateTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTeamMemberRequest) => {
      const response = await apiClient.post<TeamMember>('/api/hr/team-members/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
    },
  });
};

export const useUpdateTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TeamMember> }) => {
      const response = await apiClient.patch<TeamMember>(`/api/hr/team-members/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      queryClient.invalidateQueries({ queryKey: ['team-member', id] });
    },
  });
};

export const useDeleteTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/hr/team-members/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
    },
  });
};

export const useTerminateTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, termination_date, notes }: { id: string; termination_date?: string; notes?: string }) => {
      const response = await apiClient.post<TeamMember>(`/api/hr/team-members/${id}/terminate/`, {
        termination_date,
        notes,
      });
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      queryClient.invalidateQueries({ queryKey: ['team-member', id] });
    },
  });
};

export const useInviteNewHire = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: InviteNewHireRequest) => {
      const response = await apiClient.post<InviteNewHireResponse>('/api/hr/team-members/invite/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-sessions'] });
    },
  });
};

export interface OnboardingLinkResponse {
  onboarding_url: string;
  expires_at: string;
  session_status: string;
}

export const useGetOnboardingLink = () => {
  return useMutation({
    mutationFn: async (memberId: string) => {
      const response = await apiClient.get<OnboardingLinkResponse>(
        `/api/hr/team-members/${memberId}/onboarding-link/`
      );
      return response.data;
    },
  });
};

// ============================================================================
// Onboarding Package Hooks
// ============================================================================

export const useOnboardingPackages = () => {
  return useQuery({
    queryKey: ['onboarding-packages'],
    queryFn: async () => {
      return loadHrPages<OnboardingPackageListItem>('/api/hr/onboarding-packages/');
    },
  });
};

export const useOnboardingPackage = (id: string) => {
  return useQuery({
    queryKey: ['onboarding-package', id],
    queryFn: async () => {
      const response = await apiClient.get<OnboardingPackage>(`/api/hr/onboarding-packages/${id}/`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useCreateOnboardingPackage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateOnboardingPackageRequest) => {
      const response = await apiClient.post<OnboardingPackage>('/api/hr/onboarding-packages/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-packages'] });
    },
  });
};

export const useUpdateOnboardingPackage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreateOnboardingPackageRequest> }) => {
      const response = await apiClient.patch<OnboardingPackage>(`/api/hr/onboarding-packages/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-packages'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-package', id] });
    },
  });
};

export const useDeleteOnboardingPackage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/hr/onboarding-packages/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-packages'] });
    },
  });
};

export const useDuplicateOnboardingPackage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<OnboardingPackage>(`/api/hr/onboarding-packages/${id}/duplicate/`, {});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-packages'] });
    },
  });
};

// ============================================================================
// Onboarding Session Hooks
// ============================================================================

export const useOnboardingSessions = (filters?: { status?: string; page?: number; page_size?: number; search?: string; queue?: string; owner?: string }) => {
  return useQuery({
    queryKey: ['onboarding-sessions', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      for (const key of ["search", "queue", "owner"] as const) if (filters?.[key]) params.set(key, filters[key]);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.page_size) params.append('page_size', filters.page_size.toString());

      const response = await apiClient.get<OnboardingSessionListItem[] | PaginatedResponse<OnboardingSessionListItem>>(
        `/api/hr/onboarding-sessions/?${params.toString()}`
      );
      const data = response.data;
      // The dashboard counts statuses off `count`, so keep the envelope.
      if (Array.isArray(data)) {
        return { count: data.length, results: data, next: null, previous: null } as PaginatedResponse<OnboardingSessionListItem>;
      }
      return data;
    },
  });
};

export const useOnboardingSession = (id: string) => {
  return useQuery({
    queryKey: ['onboarding-session', id],
    queryFn: async () => {
      const response = await apiClient.get<OnboardingSession>(`/api/hr/onboarding-sessions/${id}/`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useResendOnboardingInvite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<{ message: string; access_token: string }>(
        `/api/hr/onboarding-sessions/${id}/resend-invite/`,
        {}
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-session', id] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-sessions'] });
    },
  });
};

export const useRevokeOnboardingAccess = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const response = await apiClient.post<{ message: string }>(
        `/api/hr/onboarding-sessions/${id}/revoke-access/`,
        { reason }
      );
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-session', id] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-sessions'] });
    },
  });
};

export const useApproveOnboarding = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const response = await apiClient.post<OnboardingSession>(
        `/api/hr/onboarding-sessions/${id}/approve/`,
        { notes }
      );
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-session', id] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
    },
  });
};

// ============================================================================
// Public Onboarding Wizard Hooks (No Auth Required)
// ============================================================================

export const usePublicOnboardingSession = (token: string) => {
  return useQuery({
    queryKey: ['public-onboarding-session', token],
    queryFn: async () => {
      const response = await apiClient.get<PublicOnboardingSession>(
        `/api/hr/onboarding/session/${token}/`
      );
      return response.data;
    },
    enabled: !!token,
    retry: false,
  });
};

export const useSaveOnboardingProgress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      token,
      stepId,
      data,
    }: {
      token: string;
      stepId: string;
      data: Record<string, unknown>;
    }) => {
      const response = await apiClient.post<{ status: string; step_id: string }>(
        `/api/hr/onboarding/session/${token}/step/${stepId}/progress/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, { token }) => {
      queryClient.invalidateQueries({ queryKey: ['public-onboarding-session', token] });
    },
  });
};

export const useCompleteOnboardingStep = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      token,
      stepId,
      data,
    }: {
      token: string;
      stepId: string;
      data?: Record<string, unknown>;
    }) => {
      const response = await apiClient.post<{
        status: string;
        step_id: string;
        next_step: string | null;
        completion_percentage: number;
      }>(`/api/hr/onboarding/session/${token}/step/${stepId}/complete/`, data || {});
      return response.data;
    },
    onSuccess: (_, { token }) => {
      queryClient.invalidateQueries({ queryKey: ['public-onboarding-session', token] });
    },
  });
};

export const useSubmitOnboarding = (token?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tokenArg?: string) => {
      const finalToken = tokenArg || token;
      if (!finalToken) throw new Error('Token is required');
      const response = await apiClient.post<{ status: string; message: string }>(
        `/api/hr/onboarding/session/${finalToken}/submit/`,
        {}
      );
      return response.data;
    },
    onSuccess: () => {
      if (token) {
        queryClient.invalidateQueries({ queryKey: ['public-onboarding-session', token] });
      }
    },
  });
};

// Convenience hooks that take token upfront for the wizard
export const useSaveStepProgress = (token: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ stepId, data }: { stepId: string; data: Record<string, unknown> }) => {
      const response = await apiClient.post<{ status: string; step_id: string }>(
        `/api/hr/onboarding/session/${token}/step/${stepId}/progress/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['public-onboarding-session', token] });
    },
  });
};

export const useCompleteStep = (token: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ stepId, data }: { stepId: string; data?: Record<string, unknown> }) => {
      const response = await apiClient.post<{
        status: string;
        step_id: string;
        next_step: string | null;
        completion_percentage: number;
      }>(`/api/hr/onboarding/session/${token}/step/${stepId}/complete/`, data || {});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['public-onboarding-session', token] });
    },
  });
};

export const useUploadOnboardingDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      token,
      stepId,
      file,
      documentType,
      name,
    }: {
      token: string;
      stepId: string;
      file: File;
      documentType: string;
      name?: string;
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);
      if (name) formData.append('name', name);

      const response = await apiClient.post(
        `/api/hr/onboarding/session/${token}/step/${stepId}/upload/`,
        formData
      );
      return response.data;
    },
    onSuccess: (_, { token }) => {
      queryClient.invalidateQueries({ queryKey: ['public-onboarding-session', token] });
    },
  });
};

/**
 * Sign one of the package's required documents. The response is the same
 * document object the session payload carries, with the signed state filled in.
 */
export const useSignOnboardingDocument = (token: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      data,
    }: {
      templateId: string;
      data: SignOnboardingDocumentRequest;
    }) => {
      const response = await apiClient.post<SignOnboardingDocumentResponse>(
        `/api/hr/onboarding/session/${token}/documents/${templateId}/sign/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['public-onboarding-session', token] });
    },
  });
};

// ============================================================================
// Training Library Hooks
// ============================================================================

export async function loadHrPages<T>(endpoint: string): Promise<T[]> {
  const rows: T[] = [];
  for (let page = 1; page <= 100; page++) {
    const { data } = await apiClient.get<T[] | PaginatedResponse<T>>(`${endpoint}${endpoint.includes('?') ? '&' : '?'}page=${page}&page_size=100`);
    if (Array.isArray(data)) return data;
    rows.push(...data.results);
    if (!data.next) return rows;
  }
  throw new Error('This collection is too large to load. Narrow the selection.');
}

export const useTrainingLibrary = (filters?: { content_type?: string; category?: string; is_active?: boolean }) => {
  return useQuery({
    queryKey: ['training-library', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.content_type) params.append('content_type', filters.content_type);
      if (filters?.category) params.append('category', filters.category);
      if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));

      return loadHrPages<TrainingLibraryItemListItem>(`/api/training/library/?${params.toString()}`);
    },
  });
};

export const useTrainingItem = (id: string) => {
  return useQuery({
    queryKey: ['training-item', id],
    queryFn: async () => {
      const response = await apiClient.get<TrainingLibraryItem>(`/api/training/library/${id}/`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useCreateTrainingItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTrainingItemRequest) => {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (value instanceof File) {
            formData.append(key, value);
          } else if (Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });

      const response = await apiClient.post<TrainingLibraryItem>('/api/training/library/', formData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-library'] });
    },
  });
};

export const useUpdateTrainingItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Omit<Partial<TrainingLibraryItem>, 'file' | 'thumbnail'> & { file?: File; thumbnail?: File; clear_file?: boolean; revision_policy?: string } }) => {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (value instanceof File) {
            formData.append(key, value);
          } else if (Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });

      const response = await apiClient.patch<TrainingLibraryItem>(`/api/training/library/${id}/`, formData);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['training-library'] });
      queryClient.invalidateQueries({ queryKey: ['training-item', id] });
    },
  });
};

export const useDeleteTrainingItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/training/library/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-library'] });
    },
  });
};

export const useTrainingCategories = () => {
  return useQuery({
    queryKey: ['training-categories'],
    queryFn: async () => {
      const response = await apiClient.get<string[]>('/api/training/library/categories/');
      return response.data;
    },
  });
};

// ============================================================================
// Training Assignment Hooks
// ============================================================================

export const useTrainingAssignments = () => {
  return useQuery({
    queryKey: ['training-assignments'],
    queryFn: async () => {
      return loadHrPages<TrainingAssignmentListItem>('/api/training/assignments/');
    },
  });
};

export const useTrainingAssignment = (id: string) => {
  return useQuery({
    queryKey: ['training-assignment', id],
    queryFn: async () => {
      const response = await apiClient.get<TrainingAssignment>(`/api/training/assignments/${id}/`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useCreateTrainingAssignment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTrainingAssignmentRequest) => {
      const response = await apiClient.post<TrainingAssignment>('/api/training/assignments/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-assignments'] });
    },
  });
};

export const useUpdateTrainingAssignment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreateTrainingAssignmentRequest> }) => {
      const response = await apiClient.patch<TrainingAssignment>(`/api/training/assignments/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['training-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['training-assignment', id] });
    },
  });
};

export const useDeleteTrainingAssignment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/training/assignments/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-assignments'] });
    },
  });
};

export const useBulkAssignTraining = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { training_item_ids: string[]; team_member_ids: string[]; due_date?: string }) => {
      const response = await apiClient.post<{ message: string; created_count: number }>(
        '/api/training/assignments/bulk-assign/',
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-progress'] });
    },
  });
};

// ============================================================================
// Training Progress Hooks
// ============================================================================

export const useTrainingProgress = (filters?: { team_member?: string; training_item?: string; status?: string; is_overdue?: boolean }) => {
  return useQuery({
    queryKey: ['training-progress', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.team_member) params.append('team_member', filters.team_member);
      if (filters?.training_item) params.append('training_item', filters.training_item);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.is_overdue !== undefined) params.append('is_overdue', String(filters.is_overdue));

      const response = await apiClient.get<TrainingProgress[] | PaginatedResponse<TrainingProgress>>(
        `/api/training/progress/?${params.toString()}`
      );
      const data = response.data;
      return Array.isArray(data) ? data : data.results;
    },
  });
};

export const useTrainingProgressSummary = () => {
  return useQuery({
    queryKey: ['training-progress-summary'],
    queryFn: async () => {
      const response = await apiClient.get<TrainingProgressSummary>('/api/training/progress/summary/');
      return response.data;
    },
  });
};

// ============================================================================
// Training Certificate Hooks
// ============================================================================

export const useTrainingCertificates = (teamMemberId?: string) => {
  return useQuery({
    queryKey: ['training-certificates', teamMemberId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (teamMemberId) params.append('team_member', teamMemberId);

      const response = await apiClient.get<TrainingCertificate[] | PaginatedResponse<TrainingCertificate>>(
        `/api/training/certificates/?${params.toString()}`
      );
      const data = response.data;
      return Array.isArray(data) ? data : data.results;
    },
  });
};

// ============================================================================
// Public Training Hooks (for new hire wizard)
// ============================================================================

export const usePublicAssignedTraining = (token: string) => {
  return useQuery({
    queryKey: ['public-training', token],
    queryFn: async () => {
      const response = await apiClient.get<PublicTrainingProgress[]>(
        `/api/training/new-hire/items/${token}/`
      );
      return response.data;
    },
    enabled: !!token,
    retry: false,
  });
};

export const usePublicTrainingContent = (token: string, trainingId: string) => {
  return useQuery({
    queryKey: ['public-training-content', token, trainingId],
    queryFn: async () => {
      const response = await apiClient.get<{
        training_item: TrainingLibraryItem;
        progress: PublicTrainingProgress;
        file_url: string | null;
        structured_content: unknown[];
      }>(`/api/training/new-hire/content/${token}/${trainingId}/`);
      return response.data;
    },
    enabled: !!token && !!trainingId,
    retry: false,
  });
};

export const useUpdatePublicTrainingProgress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      token,
      trainingId,
      watchPercentage,
      progressData,
    }: {
      token: string;
      trainingId: string;
      watchPercentage?: number;
      progressData?: Record<string, unknown>;
    }) => {
      const response = await apiClient.post<PublicTrainingProgress>(
        `/api/training/new-hire/progress/${token}/${trainingId}/`,
        { watch_percentage: watchPercentage, progress_data: progressData }
      );
      return response.data;
    },
    onSuccess: (_, { token }) => {
      queryClient.invalidateQueries({ queryKey: ['public-training', token] });
    },
  });
};

export const useCompletePublicTraining = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      token,
      trainingId,
      score,
      quizAnswers,
      checklistCompleted,
    }: {
      token: string;
      trainingId: string;
      score?: number;
      quizAnswers?: Record<string, string>;
      checklistCompleted?: string[];
    }) => {
      const response = await apiClient.post<{
        status: string;
        score?: number;
        passed: boolean;
        progress: PublicTrainingProgress;
      }>(`/api/training/new-hire/complete/${token}/${trainingId}/`, {
        score,
        quiz_answers: quizAnswers,
        checklist_completed: checklistCompleted,
      });
      return response.data;
    },
    onSuccess: (_, { token }) => {
      queryClient.invalidateQueries({ queryKey: ['public-training', token] });
    },
  });
};

// ============================================================================
// HR Settings Hooks
// ============================================================================

export const useHrSettings = () => {
  return useQuery({
    queryKey: ['hr-settings'],
    queryFn: async () => {
      const response = await apiClient.get<HrSettings>('/api/hr/settings/');
      return response.data;
    },
  });
};

export const useUpdateHrSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateHrSettingsRequest) => {
      const response = await apiClient.patch<HrSettings>('/api/hr/settings/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr-settings'] });
    },
  });
};

// ============================================================================
// Training Item Duplication Hook
// ============================================================================

export const useDuplicateTrainingItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<TrainingLibraryItem>(`/api/training/library/${id}/duplicate/`, {});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-library'] });
    },
  });
};

// ============================================================================
// Recruiting Autopilot Hooks
// ============================================================================

import type {
  ApplicationResponsePayload,
  RecruitingCampaign,
  RecruitingCampaignListItem,
  CreateCampaignRequest,
  UpdateCampaignRequest,
  Candidate,
  CandidateListItem,
  CandidateInterview,
  CampaignStats,
  QualificationQuestion,
  PublicCampaignInfo,
  ApplicationSubmitRequest,
  ApplicationSubmitResponse,
  AdvanceStageRequest,
  RejectCandidateRequest,
  SendMessageRequest,
  OverrideScoreRequest,
  CompleteInterviewRequest,
  ScheduleInterviewRequest,
  HireCandidateRequest,
  HireCandidateResponse,
  CampaignStatus,
  CandidateStage,
} from '../types/hr';

// Campaign Hooks
export const useRecruitingCampaigns = (status?: CampaignStatus, page = 1, search = '') => {
  return useQuery({
    queryKey: ['recruiting-campaigns', status, page, search],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), search });
      if (status) params.append('status', status);
      const response = await apiClient.get<RecruitingCampaignListItem[] | PaginatedResponse<RecruitingCampaignListItem>>(
        `/api/hr/recruiting/campaigns/?${params.toString()}`
      );
      const data = response.data;
      return Array.isArray(data) ? { results: data, count: data.length, next: null, previous: null } : data;
    },
  });
};

export const useRecruitingCampaign = (id: string) => {
  return useQuery({
    queryKey: ['recruiting-campaign', id],
    queryFn: async () => {
      const response = await apiClient.get<RecruitingCampaign>(`/api/hr/recruiting/campaigns/${id}/`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useCreateRecruitingCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCampaignRequest) => {
      const response = await apiClient.post<RecruitingCampaign>('/api/hr/recruiting/campaigns/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recruiting-campaigns'] });
    },
  });
};

export const useUpdateRecruitingCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateCampaignRequest }) => {
      const response = await apiClient.patch<RecruitingCampaign>(`/api/hr/recruiting/campaigns/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['recruiting-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['recruiting-campaign', id] });
    },
  });
};

export const useDeleteRecruitingCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/hr/recruiting/campaigns/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recruiting-campaigns'] });
    },
  });
};

export const useLaunchCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<{ message: string; status: string }>(
        `/api/hr/recruiting/campaigns/${id}/launch/`,
        {}
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['recruiting-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['recruiting-campaign', id] });
    },
  });
};

export const usePauseCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<{ message: string; status: string }>(
        `/api/hr/recruiting/campaigns/${id}/pause/`,
        {}
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['recruiting-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['recruiting-campaign', id] });
    },
  });
};

export const useCloseCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<{ message: string; status: string }>(
        `/api/hr/recruiting/campaigns/${id}/close/`,
        {}
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['recruiting-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['recruiting-campaign', id] });
    },
  });
};

export const useCampaignCandidates = (campaignId: string, stage?: CandidateStage, filters: Record<string, string> = {}) => {
  return useQuery({
    queryKey: ['campaign-candidates', campaignId, stage, filters],
    queryFn: async () => {
      const params = new URLSearchParams(filters);
      if (stage) params.append('stage', stage);
      const response = await apiClient.get<CandidateListItem[]>(
        `/api/hr/recruiting/campaigns/${campaignId}/candidates/?${params.toString()}`
      );
      return response.data;
    },
    enabled: !!campaignId,
  });
};

export const useCampaignStats = (campaignId: string) => {
  return useQuery({
    queryKey: ['campaign-stats', campaignId],
    queryFn: async () => {
      const response = await apiClient.get<CampaignStats>(
        `/api/hr/recruiting/campaigns/${campaignId}/stats/`
      );
      return response.data;
    },
    enabled: !!campaignId,
  });
};

export const useCampaignQuestions = (campaignId: string) => {
  return useQuery({
    queryKey: ['campaign-questions', campaignId],
    queryFn: async () => {
      const response = await apiClient.get<QualificationQuestion[]>(
        `/api/hr/recruiting/campaigns/${campaignId}/questions/`
      );
      return response.data;
    },
    enabled: !!campaignId,
  });
};

export const useUpdateCampaignQuestions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      campaignId,
      questions
    }: {
      campaignId: string;
      questions: Partial<QualificationQuestion>[];
    }) => {
      const response = await apiClient.put<QualificationQuestion[]>(
        `/api/hr/recruiting/campaigns/${campaignId}/questions/`,
        { questions }
      );
      return response.data;
    },
    onSuccess: (_, { campaignId }) => {
      queryClient.invalidateQueries({ queryKey: ['campaign-questions', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['recruiting-campaign', campaignId] });
    },
  });
};

// Candidate Hooks
export const useCandidates = (filters?: { campaign?: string; stage?: CandidateStage }) => {
  return useQuery({
    queryKey: ['candidates', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.campaign) params.append('campaign', filters.campaign);
      if (filters?.stage) params.append('stage', filters.stage);
      const response = await apiClient.get<CandidateListItem[] | PaginatedResponse<CandidateListItem>>(
        `/api/hr/recruiting/candidates/?${params.toString()}`
      );
      const data = response.data;
      return Array.isArray(data) ? data : data.results;
    },
  });
};

export const useCandidate = (id: string) => {
  return useQuery({
    queryKey: ['candidate', id],
    queryFn: async () => {
      const response = await apiClient.get<Candidate>(`/api/hr/recruiting/candidates/${id}/`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useUpdateCandidate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Candidate> }) => {
      const response = await apiClient.patch<Candidate>(`/api/hr/recruiting/candidates/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      queryClient.invalidateQueries({ queryKey: ['candidate', id] });
      queryClient.invalidateQueries({ queryKey: ['campaign-candidates'] });
    },
  });
};

export const useAdvanceCandidate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: AdvanceStageRequest }) => {
      const response = await apiClient.post<{ message: string; stage: string }>(
        `/api/hr/recruiting/candidates/${id}/advance/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      queryClient.invalidateQueries({ queryKey: ['candidate', id] });
      queryClient.invalidateQueries({ queryKey: ['campaign-candidates'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-stats'] });
    },
  });
};

export const useRejectCandidate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data?: RejectCandidateRequest }) => {
      const response = await apiClient.post<{ message: string; stage: string }>(
        `/api/hr/recruiting/candidates/${id}/reject/`,
        data || {}
      );
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      queryClient.invalidateQueries({ queryKey: ['candidate', id] });
      queryClient.invalidateQueries({ queryKey: ['campaign-candidates'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-stats'] });
    },
  });
};

export const useSendCandidateMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SendMessageRequest }) => {
      const response = await postCommunication<{ message: string; status: string; error?: string }>(
        `/api/hr/recruiting/candidates/${id}/message/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['candidate', id] });
    },
  });
};

export const useSendSchedulingLink = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await postCommunication<{ booking_link: string; message: string; status?: string; error?: string }>(
        `/api/hr/recruiting/candidates/${id}/schedule-link/`,
        {}
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['candidate', id] });
    },
  });
};

export const useOverrideCandidateScore = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: OverrideScoreRequest }) => {
      const response = await apiClient.patch<{ message: string; effective_score: number }>(
        `/api/hr/recruiting/candidates/${id}/score/override/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['candidate', id] });
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-candidates'] });
    },
  });
};

export const useHireCandidate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: HireCandidateRequest }) => {
      const response = await apiClient.post<HireCandidateResponse>(
        `/api/hr/recruiting/candidates/${id}/hire/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-candidates'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-stats'] });
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
    },
  });
};

// Interview Hooks
export const useCandidateInterviews = (filters?: { candidate?: string; interviewer?: string; status?: string }) => {
  return useQuery({
    queryKey: ['candidate-interviews', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.candidate) params.append('candidate', filters.candidate);
      if (filters?.interviewer) params.append('interviewer', filters.interviewer);
      if (filters?.status) params.append('status', filters.status);
      const response = await apiClient.get<CandidateInterview[] | PaginatedResponse<CandidateInterview>>(
        `/api/hr/recruiting/interviews/?${params.toString()}`
      );
      const data = response.data;
      return Array.isArray(data) ? data : data.results;
    },
  });
};

export const useScheduleInterview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ScheduleInterviewRequest & { candidate_id?: string }) => {
      const { candidate_id, ...rest } = data;
      const response = await apiClient.post<CandidateInterview>(
        '/api/hr/recruiting/interviews/',
        {
          ...rest,
          candidate: data.candidate ?? candidate_id,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidate-interviews'] });
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-candidates'] });
    },
  });
};

export const useCompleteInterview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CompleteInterviewRequest }) => {
      const response = await apiClient.patch<{ message: string; decision: string }>(
        `/api/hr/recruiting/interviews/${id}/complete/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidate-interviews'] });
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-candidates'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-stats'] });
    },
  });
};

export const useMarkInterviewNoShow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<{ message: string }>(
        `/api/hr/recruiting/interviews/${id}/no-show/`,
        {}
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidate-interviews'] });
    },
  });
};

// Public Apply Page Hooks (no auth required)
export const usePublicCampaign = (slug: string) => {
  return useQuery({
    queryKey: ['public-campaign', slug],
    queryFn: async () => {
      const response = await fetch(`${window.location.origin}/api/hr/recruiting/apply/${slug}/`);
      if (!response.ok) {
        const error = new Error(`HTTP error! status: ${response.status}`);
        (error as { status?: number }).status = response.status;
        throw error;
      }
      return response.json() as Promise<PublicCampaignInfo>;
    },
    enabled: !!slug,
    retry: false,
  });
};

export const useSubmitApplication = () => {
  return useMutation({
    mutationFn: async ({ slug, data }: { slug: string; data: ApplicationSubmitRequest }) => {
      const formData = new FormData();
      if (data.assessment_token) formData.append('assessment_token', data.assessment_token);
      formData.append('first_name', data.first_name);
      formData.append('last_name', data.last_name);
      formData.append('email', data.email);
      formData.append('phone', data.phone);
      formData.append('sms_consent', String(data.sms_consent));
      if (data.source) formData.append('source', data.source);
      if (data.resume) formData.append('resume', data.resume);

      // Screening answers. FormData cannot carry nested data alongside a file,
      // so they go as a JSON string, which the submit serializer parses. This
      // list was previously built by the page and never appended here, so every
      // answer a candidate gave was dropped before the request left the browser.
      const responses = (data as ApplicationSubmitRequest & {
        responses?: ApplicationResponsePayload[];
      }).responses;
      if (responses && responses.length > 0) {
        formData.append('responses', JSON.stringify(responses));
      }

      const response = await fetch(`${window.location.origin}/api/hr/recruiting/apply/${slug}/submit/`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || error.detail || JSON.stringify(error));
      }

      return response.json() as Promise<ApplicationSubmitResponse>;
    },
  });
};
